import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent } from '@ohif/ui-next';
import { X } from 'lucide-react';
import facilityService from '../../services/facilityService';
import userService from '../../services/userService';
import type { Facility } from '../../types/facility.types';
import type { UserServiceResponse } from '../../services/userService';
import type { User } from '../../types/user.types';
import { pickFromRecord } from '../../utils/facilityMapper';
import { formatDateOnly } from '../../utils/dateFormatter';
import { ICON_STROKE_WIDTH } from '../../constants';
import './FacilityDialogs/facilityDialogs.css';

function fmt(value: unknown): string {
  const s = String(value ?? '').trim();
  return s || '--';
}

function countFromUsersResponse(res: UserServiceResponse<User[]>): number | null {
  if (!res.success) {
    return null;
  }
  if (res.pagination?.totalUsers != null) {
    return res.pagination.totalUsers;
  }
  return res.data?.length ?? 0;
}

function fmtUserCount(value: number | null | undefined, loading: boolean): string {
  if (loading) {
    return '…';
  }
  if (value == null) {
    return '--';
  }
  return String(value);
}

export default function FacilityDetailsDialog({
  open,
  facilityId,
  onOpenChange,
}: {
  open: boolean;
  facilityId?: number | string;
  onOpenChange: (open: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [userStatsLoading, setUserStatsLoading] = useState(false);
  const [userStats, setUserStats] = useState<{
    total: number | null;
    radiologists: number | null;
    technicians: number | null;
  } | null>(null);

  useEffect(() => {
    if (!open || facilityId == null) {
      setUserStats(null);
      setUserStatsLoading(false);
      return;
    }
    let cancelled = false;
    setUserStatsLoading(true);
    setUserStats(null);
    void Promise.all([
      userService.getAllUsers(1, facilityId, { status: 'Active' }),
      userService.getAllUsers(1, facilityId, { role: 'Radiologist', status: 'Active' }),
      userService.getAllUsers(1, facilityId, { role: 'Technician', status: 'Active' }),
    ])
      .then(([allActive, radiologistsRes, techniciansRes]) => {
        if (cancelled) {
          return;
        }
        setUserStats({
          total: countFromUsersResponse(allActive),
          radiologists: countFromUsersResponse(radiologistsRes),
          technicians: countFromUsersResponse(techniciansRes),
        });
      })
      .finally(() => {
        if (!cancelled) {
          setUserStatsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, facilityId]);

  useEffect(() => {
    if (!open) {
      setFacility(null);
      return;
    }
    if (facilityId == null) {
      return;
    }
    let mounted = true;
    setLoading(true);
    facilityService
      .getFacilityById(facilityId)
      .then(res => {
        if (!mounted) {
          return;
        }
        setFacility(res.success ? (res.data as Facility) : null);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [open, facilityId]);

  const root = useMemo(
    () => (facility ? (facility as unknown as Record<string, unknown>) : null),
    [facility]
  );

  const facilityName = useMemo(() => fmt(pickFromRecord(root, 'facility_name', 'name')), [root]);
  const facilityCode = useMemo(() => fmt(pickFromRecord(root, 'facility_code', 'code')), [root]);
  const facilityType = useMemo(() => fmt(pickFromRecord(root, 'facility_type', 'type')), [root]);
  const status = useMemo(() => fmt(pickFromRecord(root, 'status')), [root]);
  const contactPhone = useMemo(
    () => fmt(pickFromRecord(root, 'contact_number', 'contactNumber', 'phoneNo', 'phone_no')),
    [root]
  );
  const contactEmail = useMemo(
    () => fmt(pickFromRecord(root, 'email_id', 'emailId', 'email')),
    [root]
  );

  const addressLines = useMemo(() => {
    if (!root) {
      return ['--'];
    }
    const l1 = fmt(pickFromRecord(root, 'address_line_1', 'addressLine1'));
    const l2 = fmt(pickFromRecord(root, 'address_line_2', 'addressLine2'));
    const city = fmt(pickFromRecord(root, 'city'));
    const state = fmt(pickFromRecord(root, 'state'));
    const pin = fmt(pickFromRecord(root, 'pincode', 'pin_code'));
    const parts = [l1, l2].filter(x => x && x !== '--');
    const tail = [city, state, pin].filter(x => x && x !== '--').join(', ');
    if (tail) {
      parts.push(tail);
    }
    return parts.length ? parts : ['--'];
  }, [root]);

  const createdByName = useMemo(() => {
    if (!facility) {
      return '--';
    }
    const named = facility.CreatedBy?.full_name?.trim();
    if (named) {
      return named;
    }
    return fmt(pickFromRecord(root, 'createdBy', 'created_by'));
  }, [facility, root]);

  const modifiedByName = useMemo(() => {
    if (!facility) {
      return '--';
    }
    const named = facility.ModifiedBy?.full_name?.trim();
    if (named) {
      return named;
    }
    return fmt(pickFromRecord(root, 'lastModifiedBy', 'modified_by'));
  }, [facility, root]);

  const createdAtDate = useMemo(() => {
    if (!root) {
      return '--';
    }
    const raw = pickFromRecord(root, 'created_at', 'createdAt');
    const s = raw != null && raw !== '' ? String(raw).trim() : '';
    return s ? formatDateOnly(s) || '--' : '--';
  }, [root]);

  const lastModifiedDate = useMemo(() => {
    if (!root) {
      return '--';
    }
    const raw = pickFromRecord(root, 'updated_at', 'updatedAt');
    const s = raw != null && raw !== '' ? String(raw).trim() : '';
    return s ? formatDateOnly(s) || '--' : '--';
  }, [root]);

  /** Title line: "Facility Details-" + facility code (fallback to name if code missing). */
  const headerNamePart = useMemo(() => {
    if (loading) {
      return '…';
    }
    if (facilityCode && facilityCode !== '--') {
      return facilityCode;
    }
    if (facilityName && facilityName !== '--') {
      return facilityName;
    }
    return '—';
  }, [loading, facilityName, facilityCode]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      shouldCloseOnEsc={!loading}
      /* Match create/edit facility: backdrop click does not dismiss (avoid accidental close). */
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        showCloseButton={false}
        className="facility-dialog-content create-user-dialog-content facility-details-dialog-shell"
      >
        <div className="facility-details-view-header">
          <div className="facility-details-view-header-text">
            <h2 className="facility-details-view-title-line">
              <span className="facility-details-view-title-label">Facility Details-</span>
              <span className="facility-details-view-title-name">{headerNamePart}</span>
            </h2>
          </div>
          <button
            type="button"
            className="facility-details-view-close"
            onClick={() => !loading && onOpenChange(false)}
            disabled={loading}
            aria-label="Close"
          >
            <X
              className="h-5 w-5"
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
        </div>

        <div className="create-user-dialog-body facility-dialog-body">
          {loading ? <div className="facility-dialog-loading">Loading…</div> : null}

          {!loading ? (
            <>
              <div className="facility-dialog-section">
                <h3 className="facility-dialog-section-title">Basic Info</h3>
                <div className="facility-dialog-grid-2">
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Facility Name</div>
                    <div className="facility-dialog-v">{facilityName}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Facility Code</div>
                    <div className="facility-dialog-v">{facilityCode}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Facility Type</div>
                    <div className="facility-dialog-v">{facilityType}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Status</div>
                    <div className="facility-dialog-v">{status}</div>
                  </div>
                </div>
              </div>

              <div className="facility-dialog-section">
                <h3 className="facility-dialog-section-title">Address &amp; Contact</h3>
                {/* Figma 193:1194 — Address + Contact Number on first row; Email on second row (left). */}
                <div className="facility-details-address-contact-grid">
                  <div className="facility-dialog-kv facility-details-address-contact-grid__address">
                    <div className="facility-dialog-k">Address</div>
                    <div className="facility-dialog-v">
                      {addressLines.map((l, idx) => (
                        <div key={idx}>{l}</div>
                      ))}
                    </div>
                  </div>
                  <div className="facility-dialog-kv facility-details-address-contact-grid__phone">
                    <div className="facility-dialog-k">Contact Number</div>
                    <div className="facility-dialog-v">{contactPhone}</div>
                  </div>
                  <div className="facility-dialog-kv facility-details-address-contact-grid__email">
                    <div className="facility-dialog-k">Email</div>
                    <div className="facility-dialog-v">{contactEmail}</div>
                  </div>
                </div>
              </div>

              <div className="facility-dialog-section">
                <h3 className="facility-dialog-section-title">Users</h3>
                <div className="facility-dialog-users-row">
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Total Users</div>
                    <div className="facility-dialog-v">
                      {fmtUserCount(userStats?.total ?? null, userStatsLoading)}
                    </div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Radiologists</div>
                    <div className="facility-dialog-v">
                      {fmtUserCount(userStats?.radiologists ?? null, userStatsLoading)}
                    </div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Technicians</div>
                    <div className="facility-dialog-v">
                      {fmtUserCount(userStats?.technicians ?? null, userStatsLoading)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="facility-dialog-section">
                <h3 className="facility-dialog-section-title">Audit Info</h3>
                <div className="facility-dialog-grid-2">
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Created By</div>
                    <div className="facility-dialog-v">{createdByName}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Created At</div>
                    <div className="facility-dialog-v">{createdAtDate}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Last Modified By</div>
                    <div className="facility-dialog-v">{modifiedByName}</div>
                  </div>
                  <div className="facility-dialog-kv">
                    <div className="facility-dialog-k">Last Modified</div>
                    <div className="facility-dialog-v">{lastModifiedDate}</div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="create-user-dialog-footer facility-dialog-footer">
          <button
            type="button"
            className="facility-dialog-btn facility-dialog-btn--primary"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
